import { Component, OnInit } from '@angular/core';
import { TreeNode, MessageService } from 'primeng/api';
import { EqmsScopeManagementService } from '../../shared//services/eqms-scope-management.service';
import { TreeData } from '../../shared/models/TreeData';
import { eQMSScopeConstants } from '../eqms-scope-constants';
import { LabelConstants } from '../../shared/constants/label-constants';
import { EqmsScopeService } from '../eqms-scope.service';
import { EQMSScopeHierarchyWithButtonFlags } from '../../shared/models/scopes/eqms-scope-hierarchy-with-button-flags';
import { AuditTrialDetails } from '../../shared/models/audits/audit-trial-details';

@Component({
  selector: 'app-eqms-scope-management',
  templateUrl: './eqms-scope-management.component.html',
  styleUrls: ['./eqms-scope-management.component.scss'],
})
export class EqmsScopeManagementComponent implements OnInit {
  treeData: TreeNode[] = [];
  hierarchyTreeDataCopy: TreeNode[] = []; // Separate copy for hierarchy slider
  topBarTitle:string = eQMSScopeConstants.eQMSScopeManagementTitle;
  accessToeQMSScope: boolean = false;
  eQMSScopeErrorMessage!:string;
  iseQMSScopeSliderOpen: boolean = false;
  isBulkUserUpdateSliderOpen: boolean = false;
  isHierarchySliderOpen: boolean = false;
  scopeDetail: EQMSScopeHierarchyWithButtonFlags | null = null;
  isScopeSelected: boolean = false;
  selectedScopeNode: any = null;
  isLoadingAfterHierarchyUpdate: boolean = false;
  hierarchyUpdateResult: { success: boolean; errorMessage?: string } | null = null;
  scopeAuditTrail: AuditTrialDetails[] = [];
  isLoadingAuditTrail: boolean = false;
  isShowScopeAuditModal: boolean = false;
  scopeName: string = '';
  isScopeExpanded: boolean = true;
  
  get isEditButtonEnabled(): boolean {
    return this.isScopeSelected && this.selectedScopeNode?.isEnable === true;
  }

  get isAuditTrailButtonEnabled(): boolean {
    return this.isScopeSelected;
  }

  constructor(
    private eqmsScopeManagementService: EqmsScopeManagementService,
    private eqmsScopeService: EqmsScopeService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.eqmsScopeManagementService.geteQMSScopeToDisplay().subscribe({
      next: (response: EQMSScopeHierarchyWithButtonFlags) => {
        this.scopeDetail = response;
        this.accessToeQMSScope = true;
        const eQMSScopes = this.scopeDetail.scopeHierarchy;
        this.treeData.push({
          label: eQMSScopes.eQMSScopeName,
          data: eQMSScopes,
          children: eQMSScopes.children
            ? this.updateTreeNodes(eQMSScopes.children)
            : undefined,
            expanded: true
        });
      },
      error: (err) => {
        if(err.status === LabelConstants.Forbidden_StatusCode){
          this.eQMSScopeErrorMessage = err.error.detail;
          this.accessToeQMSScope = false;
        }
      }
    });

    // Subscribe to scope update events and refresh tree
    this.eqmsScopeManagementService.scopeUpdated$.subscribe((isUpdated) => {
      if (isUpdated) {
        this.refreshScopeTree();
      }
    });
  }

  private refreshScopeTree(): void {
    this.treeData = [];
    this.eqmsScopeManagementService.geteQMSScopeToDisplay().subscribe({
      next: (response: EQMSScopeHierarchyWithButtonFlags) => {
        this.scopeDetail = response;
        const eQMSScopes = this.scopeDetail.scopeHierarchy;
        this.treeData.push({
          label: eQMSScopes.eQMSScopeName,
          data: eQMSScopes,
          children: eQMSScopes.children
            ? this.updateTreeNodes(eQMSScopes.children)
            : undefined,
            expanded: true
        });
      }
    });
  }

  private updateTreeNodes(nodes: any): TreeData[] {
    let children: TreeNode[] = [];
    nodes.forEach((childNode: any) => {
      children.push({
        label: childNode.eQMSScopeName,
        data: childNode,
        children: childNode.children
          ? this.updateTreeNodes(childNode.children)
          : undefined,
          expanded: true
      });
    });
    return children;
  }

  eQMSScopeNotFoundErrorMessage() : string{
    if(!this.accessToeQMSScope)
      return this.eQMSScopeErrorMessage;

    return eQMSScopeConstants.eQMSScope_RoleAcessPermission_Fail;
  }

  createeQMSScope() {
    this.iseQMSScopeSliderOpen = true;
    this.eqmsScopeService.isEditEQMSScopeSlider = false;
  }

  openBulkUserUpdateSlider() {
    this.isBulkUserUpdateSliderOpen = true;
  }

  expandAll() {
    this.eqmsScopeManagementService.recursiveExpand(this.treeData);
    this.isScopeExpanded = true;
  }

  collapseAll() {
    this.eqmsScopeManagementService.recursiveCollapse(this.treeData, true);
    this.isScopeExpanded = false;
  }

  editeQMSScope() {
    this.iseQMSScopeSliderOpen = true;
    this.eqmsScopeService.isEditEQMSScopeSlider = true;
  }
    openHierarchySlider() {
        // Create deep copy of tree data so changes in slider don't affect main tree
        this.hierarchyTreeDataCopy = this.deepCloneTreeNodes(this.treeData);
        if(!this.isScopeExpanded){
          this.eqmsScopeManagementService.recursiveExpand(this.hierarchyTreeDataCopy);
        }
        this.isHierarchySliderOpen = true;
    }

    /**
     * Deep clone TreeNode array to prevent reference sharing
     * This ensures slider modifications don't affect the main tree until saved
     */
    private deepCloneTreeNodes(nodes: TreeNode[]): TreeNode[] {
        return nodes.map(node => ({
            ...node,
            data: node.data ? { ...node.data } : undefined,
            children: node.children ? this.deepCloneTreeNodes(node.children) : undefined
        }));
    }
  closeAndResetEQMSScopeSlider(value: boolean) {
    this.iseQMSScopeSliderOpen = false;
    // Clear selection when slider closes
    this.isScopeSelected = false;
    this.selectedScopeNode = null;
  }

  closeAndResetBulkUserScopeSlider(): void {
    this.isBulkUserUpdateSliderOpen = false;
    this.selectedScopeNode = null;
  }

  onScopeSelected(event: any) {
    this.isScopeSelected = true;
    if (event && event.node) {
      this.selectedScopeNode = event.node.data;
    }
  }

  onScopeUnselected(event: any) {
    this.isScopeSelected = false;
    this.selectedScopeNode = null;
  }

  closeAndResetHierarchySlider() {
    this.isHierarchySliderOpen = false;
    // Clear the copied tree data to free memory
    this.hierarchyTreeDataCopy = [];
  }

  onHierarchyUpdated(result: { success: boolean; errorMessage?: string }) {
    // Store the result to display toast after reload
    this.hierarchyUpdateResult = result;
    
    // Show loading spinner - keep old tree visible in background
    this.isLoadingAfterHierarchyUpdate = true;
    
    // Wait 5 seconds for cache to update before fetching fresh data
    setTimeout(() => {
      this.eqmsScopeManagementService.geteQMSScopeToDisplay().subscribe({
        next: (response: EQMSScopeHierarchyWithButtonFlags) => {
          // Clear old data before setting new data
          this.treeData = [];
          this.scopeDetail = response;
          this.accessToeQMSScope = true;
          const eQMSScopes = this.scopeDetail.scopeHierarchy;
          this.treeData.push({
            label: eQMSScopes.eQMSScopeName,
            data: { eQMSScopeId: eQMSScopes.eQMSScopeId },
            children: eQMSScopes.children
              ? this.updateTreeNodes(eQMSScopes.children)
              : undefined,
              expanded: true
          });
          // Hide loading spinner after data is loaded
          this.isLoadingAfterHierarchyUpdate = false;
          
          // Show toast message after data is fully loaded
          this.showHierarchyUpdateToast();
        },
        error: (err) => {
          console.error('Error reloading scope hierarchy:', err);
          // Hide loading spinner even on error
          this.isLoadingAfterHierarchyUpdate = false;
          
          // Show toast message even on error
          this.showHierarchyUpdateToast();
        }
      });
    }, 5000); // 5 second delay for cache refresh
  }
  
  private showHierarchyUpdateToast(): void {
    if (this.hierarchyUpdateResult) {
      if (this.hierarchyUpdateResult.success) {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Scope hierarchy updated successfully.',
          life: 5000
        });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.hierarchyUpdateResult.errorMessage || 'Failed to update scope hierarchy!',
          life: 8000
        });
      }
      // Clear the result after showing toast
      this.hierarchyUpdateResult = null;
    }
  }

  viewScopeAuditTrail() {
    if (this.selectedScopeNode && this.selectedScopeNode.eQMSScopeId) {
      this.scopeName = this.selectedScopeNode.eQMSScopeName || '';
      this.scopeAuditTrail = [];  // Clear previous data
      this.isLoadingAuditTrail = true;
      this.eqmsScopeService.getScopeAuditTrail(this.selectedScopeNode.eQMSScopeId).subscribe({
        next: (response: AuditTrialDetails[]) => {
          this.scopeAuditTrail = response?.filter(item => item.attributeDetails && item.attributeDetails.length > 0) || [];
          this.isLoadingAuditTrail = false;
          this.isShowScopeAuditModal = true;
        },
        error: (err) => {
          this.isLoadingAuditTrail = false;
          this.scopeAuditTrail = []; // Ensure cleared on error
          
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load audit trail',
            life: 5000
          });
        }
      });
    }
  }

  closeScopeAuditModal(isClosed: boolean) {
    this.isShowScopeAuditModal = isClosed;
  }
}
